import { useQuery } from '@tanstack/react-query'
import { topliApi } from '@/shared/api/topli/TopliApi'
import { UserFilters } from '../model/types'
import { createDateRangeFilter } from '@/shared/lib/utils'

export const selectUserByIdQueryKey = ['selectUserById']
export const usersQueryKey = ['users']
export const usersByPageQueryKey = 'usersByPage'
export const searchUsersQueryKey = 'searchUsers'

export const fetchUserById = async (id?: number) => {
  if (!id) throw new Error('No user')
  const response = await topliApi.user.get(id)
  return response
}

export const fetchUserList = async (filters: UserFilters = {}) => {
  const response = await topliApi.user.list(filters)
  return response
}

export const fetchUserListByPage = async (
  sorting: string | null,
  limit: number,
  offset: number,
  search: string,
  dateRange?: (string | null)[],
  statuses?: number[],
  roles?: number[],
  selectedPersonalManagerIds?: number[],
  searchById?: string,
  searchByEmail?: string,
  searchByPhone?: string,
) => {
  const [startDate, endDate] = dateRange || []

  const response = await topliApi.user.list({
    limit,
    offset,
    sort: sorting,
    fullname__icontains: search,
    ...createDateRangeFilter('created_at', startDate, endDate),
    ...(statuses?.length && { status__in: statuses }),
    ...(roles?.length && { role__in: roles }),
    ...(selectedPersonalManagerIds?.length && {
      personal_manager_id__in: selectedPersonalManagerIds,
    }),
    ...(searchById?.length && { id__icontains: searchById }),
    ...(searchByEmail?.length && { email__icontains: searchByEmail }),
    ...(searchByPhone?.length && { phone__icontains: searchByPhone }),
  })
  return response
}

export const searchUsersByName = async (search?: string, isDefaultManager?: boolean) => {
  if (!search) return
  const response = await topliApi.user.list({
    fullname__icontains: search,
    is_default_manager: isDefaultManager,
  })
  return response
}

export const useSearchUsersByName = (search?: string, isDefaultManager?: boolean) => {
  const data = useQuery({
    queryKey: [searchUsersQueryKey, search, isDefaultManager],
    queryFn: () => searchUsersByName(search, isDefaultManager),
    enabled: !!search,
  })
  return data
}

export const useUserById = (id?: number) =>
  useQuery({
    queryKey: [selectUserByIdQueryKey, id],
    queryFn: () => fetchUserById(id),
    staleTime: 0,
  })

export const useUserList = (filters: UserFilters = {}) =>
  useQuery({
    queryKey: [usersQueryKey, filters],
    queryFn: () => fetchUserList(filters),
  })

export const useUsersByPage = (
  sorting: string | null,
  limit: number,
  page: number,
  search: string,
  dateRange?: (string | null)[],
  statuses?: number[],
  roles?: number[],
  selectedPersonalManagerIds?: number[],
  searchById?: string,
  searchByEmail?: string,
  searchByPhone?: string,
) =>
  useQuery({
    queryKey: [
      usersByPageQueryKey,
      sorting,
      page,
      limit,
      search || '',
      dateRange,
      statuses,
      roles,
      selectedPersonalManagerIds,
      searchById,
      searchByEmail,
      searchByPhone,
    ],
    queryFn: () =>
      fetchUserListByPage(
        sorting,
        limit,
        page,
        search,
        dateRange,
        statuses,
        roles,
        selectedPersonalManagerIds,
        searchById,
        searchByEmail,
        searchByPhone,
      ),
  })
